import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"
import { sessionPath } from "@/routes"

export interface RoleMismatchLandingProps {
  /** Role of the currently signed-in account (`"brand" | "agency" | "admin"`).
   *  Drives the body copy interpolation so the error message says exactly
   *  which kind of account the user is on. */
  current_role: string
  className?: string
}

/**
 * RoleMismatchLanding — rendered by `pages/Public/InviteLanding.tsx` when
 * `state="role_mismatch"` (server detected the authenticated user is not a
 * creator while opening a creator invite).
 *
 * Per UX/BE TRD: server-side role check happens BEFORE the
 * `recognised_email` branch — non-creator authenticated users (`role:
 * brand|agency|admin`) get this state, NEVER the recognised-email
 * fast-path attach.
 *
 * **CTA** posts to `signOutPath()` (DELETE /sessions/...) — clears the
 * session and returns the user to a public state where they can re-open
 * the invite from a fresh browser to create a creator account. We use a
 * `<form method="post">` with a hidden `_method=delete` field to satisfy
 * Rails' authentication-zero session destroy contract.
 */
export function RoleMismatchLanding({
  current_role,
  className,
}: RoleMismatchLandingProps) {
  const { t } = useTranslation()
  const { currentSession } = useCurrentContext()
  // Sign-out posts to the canonical session DELETE endpoint
  // (`DELETE /sessions/:id`, exposed by authentication-zero). We need the
  // active session id from shared props; if it's missing we silently omit
  // the form (server should never ship `state="role_mismatch"` without a
  // session, but defensive in case of prop drift).
  const sessionDestroyUrl = currentSession
    ? sessionPath(currentSession.id)
    : null
  return (
    <div
      data-testid="invite-landing-error-role-mismatch"
      role="status"
      className={cn("flex flex-col gap-4", className)}
    >
      <h2 className="text-foreground text-xl font-semibold">
        {t("invites.landing.error.role_mismatch.heading")}
      </h2>
      <p className="text-muted-foreground text-base leading-relaxed">
        {t("invites.landing.error.role_mismatch.body", {
          role: current_role,
        })}
      </p>
      {sessionDestroyUrl && (
        <form
          method="post"
          action={sessionDestroyUrl}
          data-testid="invite-landing-error-role-mismatch-form"
        >
          <input type="hidden" name="_method" value="delete" />
          <CsrfHiddenInput />
          <Button
            type="submit"
            size="lg"
            variant="default"
            className="w-full sm:w-auto"
            data-testid="invite-landing-error-role-mismatch-cta"
          >
            {t("invites.landing.error.role_mismatch.cta")}
          </Button>
        </form>
      )}
    </div>
  )
}

function CsrfHiddenInput() {
  if (typeof document === "undefined") return null
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )
  if (!meta?.content) return null
  return <input type="hidden" name="authenticity_token" value={meta.content} />
}

export default RoleMismatchLanding
